import { decodeEntities } from './misc_functions';

/*
-------------------------------
ENRICHMENT CENTER TEXT
Some library text was pasted from Word and still holds its Windows-1252
punctuation as C1 control characters (U+0085, U+0091-U+0094, U+0096, U+0097),
which browsers draw as boxes or nothing. Every EC string from data is shown
through normalizeText; HTML-bearing fields (series descriptions, speaker bios)
through plainText.
-------------------------------
*/

const WORD_PUNCTUATION: Readonly<Record<string, string>> = {
  '\u0085': '…', // …
  '\u0091': '‘', // ‘
  '\u0092': '’', // ’
  '\u0093': '“', // “
  '\u0094': '”', // ”
  '\u0096': '–', // –
  '\u0097': '—', // —
};

const WORD_PUNCTUATION_PATTERN = /[\u0085\u0091-\u0094\u0096\u0097]/g;

/** The text with Word punctuation mapped to the characters it stands for; nothing else changes (no trim). Null -> ''. */
export const normalizeText = (text: string | null | undefined): string =>
  text == null ? '' : String(text).replace(WORD_PUNCTUATION_PATTERN, (char) => WORD_PUNCTUATION[char]);

/** SeriesDescription's isHTML test: decoded text that opens with a tag is markup. */
const OPENS_WITH_TAG = /^<([a-zA-Z]+)(\s[^>]*)?>/;

/**
 * HTML as plain text: tags stripped, entities decoded, then normalizeText.
 * Some fields hold entity-encoded HTML ('&lt;p&gt;…'), which one decodeEntities
 * pass turns into literal tags. Only when the result opens with a tag (where
 * SeriesDescription renders it as HTML) is it decoded a second time; otherwise a
 * second pass would read escaped text like 'A&lt;B' as a tag and drop it.
 * Callers judge "empty" by plainText(x).trim() === ''.
 */
export const plainText = (html: string | null | undefined): string => {
  const decoded = decodeEntities(html ?? '') ?? '';
  return normalizeText(OPENS_WITH_TAG.test(decoded) ? decodeEntities(decoded) ?? '' : decoded);
};

/** A meta line: the parts that are there (not null, undefined, false or ''), joined with ' · '. */
export const joinMeta = (parts: ReadonlyArray<string | null | undefined | false>): string =>
  parts.filter((part): part is string => typeof part === 'string' && part !== '').join(' · ');
